/**
 * Shared, SSR-safe helpers used by the per-provider matchers.
 *
 * Mirrors the behaviour of the legacy `WidgetConnectorUtil` java helper.
 */

const BASE_URL_PATTERN = /^https?:\/\/([^/]+)(?:\/.*)?$/;

/** Internal Atlassian domains forbidden as widget URLs (SSRF protection). */
export const INTERNAL_SERVICE_DOMAINS: readonly string[] = [
	'jira-dev.com',
	'atl-pass.net',
	'atlassian.net',
];

/**
 * Extracts the host portion of a URL using the same regex as the legacy
 * `DefaultRenderManager.getBaseUrl`. Falls back to the original URL if the
 * pattern does not match.
 */
export function getHost(url: string): string {
	const match = BASE_URL_PATTERN.exec(url);
	return match ? match[1] : url;
}

/**
 * Returns true if the URL targets one of the protected internal Atlassian
 * services and must NOT be embedded.
 */
export function isBlockedDomain(url: string): boolean {
	return INTERNAL_SERVICE_DOMAINS.some((domain) => url.includes(domain));
}

/**
 * Strict host + path-substring match using the URL parser. Returns false on
 * parse errors to match the conservative legacy behaviour.
 */
export function isSafeUrlMatch(
	url: string,
	expectedHost: string,
	expectedPathContains: string,
): boolean {
	try {
		const parsed = new URL(url);
		return parsed.host === expectedHost && (parsed.pathname || '').includes(expectedPathContains);
	} catch {
		return false;
	}
}

/**
 * Substring match guarded by URL parsing — mirrors `WidgetConnectorUtil.isURLMatch`.
 */
export function isUrlMatch(url: string, matchUrl: string): boolean {
	try {
		const host = new URL(url).host;
		return Boolean(host) && url.includes(matchUrl);
	} catch {
		return false;
	}
}

/**
 * If the supplied dimension is a bare integer string, append the supplied unit.
 * Otherwise return as-is so callers may pass `100%` or `300px` through.
 */
export function withUnit(value: string | undefined, unit: 'px' = 'px'): string | undefined {
	if (value === undefined || value === '') {
		return undefined;
	}
	return /^\d+$/.test(value) ? `${value}${unit}` : value;
}
